import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { formatErr } from "../lib/api";
import { Logo } from "../components/Header";

export default function SignupPage() {
  const { register } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100svh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1627761801957-4bf6cfb4fa20?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-br from-ink/40 to-ink"/>
        <div className="relative h-full flex flex-col justify-between p-12">
          <Logo/>
          <div>
            <div className="font-script text-5xl text-bone">"Plan less. Live more."</div>
            <div className="eyebrow mt-3">— Concierge Manifesto</div>
          </div>
        </div>
      </div>
      <div className="grid place-items-center px-5 py-12 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo/></div>
          <div className="eyebrow mb-3">{t("auth.signup_eyebrow", "Create account")}</div>
          <h1 className="section-title text-4xl mb-2">{t("auth.signup_title", "Join the concierge list.")}</h1>
          <p className="text-muted mb-8 text-sm">{t("auth.signup_subtitle", "A few details and you're in. No credit card required.")}</p>
          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <input required placeholder={t("auth.name_placeholder", "Full name")} value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input-bare" data-testid="signup-name"/>
            <input type="email" required placeholder={t("auth.email_placeholder", "Email")} value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input-bare" data-testid="signup-email"/>
            <input type="password" required placeholder={t("auth.password_placeholder", "Password (8+ chars)")} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="input-bare" data-testid="signup-password"/>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({...form, role: "user"})} className={`tab-pill ${form.role === "user" ? "active" : ""}`} data-testid="signup-role-user">Guest</button>
              <button type="button" onClick={() => setForm({...form, role: "provider"})} className={`tab-pill ${form.role === "provider" ? "active" : ""}`} data-testid="signup-role-provider">Provider</button>
            </div>
            {error && <div className="text-rose-300 text-sm">{error}</div>}
            <button type="submit" disabled={busy} className="btn btn-primary w-full" data-testid="signup-submit">
              {busy ? t("auth.signing_in", "Creating account…") : t("auth.create_account", "Create account")}
            </button>
          </form>
          <div className="text-sm text-muted mt-6">
            {t("auth.already_have_account", "Already have an account?")} <Link to="/login" className="text-teal hover:underline" data-testid="signup-go-login">{t("common.sign_in", "Sign in")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
